import React, { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { uploadForecast } from "../../api/forecast";
import type { WorkstreamLite } from "../../types";

export interface ForecastUploadModalProps {
  matterId: string;
  mattersInScope: WorkstreamLite[];
  onClose: () => void;
  onUploaded: (forecastRunId: string) => void;
}

interface ParsedUpload {
  fileName: string;
  name?: string;
  generatedBy?: string;
  snapshots: unknown[];
  emailIds: number[];
}

function parseForecastFile(fileName: string, text: string): ParsedUpload {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    throw new Error("not valid JSON");
  }
  const o = (doc && typeof doc === "object" && !Array.isArray(doc) ? doc : {}) as Record<string, unknown>;
  const snapshots = Array.isArray(doc) ? (doc as unknown[]) : (o.snapshots as unknown[]);
  if (!Array.isArray(snapshots) || snapshots.length === 0)
    throw new Error('no snapshots found — expected {"snapshots": [...]} or a top-level array');
  const emailIds = snapshots
    .map((s, i) => {
      const r = (s && typeof s === "object" ? s : {}) as Record<string, unknown>;
      const raw = r.emailId ?? r.email_id ?? r.id;
      const n = typeof raw === "string" ? Number(raw) : (raw as number);
      return Number.isInteger(n) && (n as number) > 0 ? (n as number) : i + 1;
    })
    .sort((a, b) => a - b);
  return {
    fileName,
    name: typeof o.name === "string" ? o.name : undefined,
    generatedBy:
      typeof o.generatedBy === "string"
        ? o.generatedBy
        : typeof o.generated_by === "string"
          ? (o.generated_by as string)
          : undefined,
    snapshots,
    emailIds,
  };
}

/* Upload modal (spec §5.4): file picker / drag-and-drop for a forecast JSON,
   parse preview, confirm to POST. Normalization happens server-side. */
export function ForecastUploadModal(props: ForecastUploadModalProps): JSX.Element {
  const { matterId, mattersInScope, onClose, onUploaded } = props;
  const [parsed, setParsed] = useState<ParsedUpload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const readFile = (file: File): void => {
    setError(null);
    file
      .text()
      .then((text) => setParsed(parseForecastFile(file.name, text)))
      .catch((e: Error) => {
        setParsed(null);
        setError(`Could not read ${file.name}: ${e.message}`);
      });
  };

  const confirm = async (): Promise<void> => {
    if (!parsed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await uploadForecast(matterId, {
        name: parsed.name || parsed.fileName.replace(/\.json$/i, ""),
        generatedBy: parsed.generatedBy,
        snapshots: parsed.snapshots,
        workstreams: mattersInScope.map((w) => ({ id: w.id, name: w.name, code: w.code })),
      });
      onUploaded(r.forecastRunId);
    } catch (e: any) {
      let msg = e?.message || "upload failed";
      try {
        const j = JSON.parse(e?.body || "");
        if (j?.error) msg = j.error;
      } catch { /* not JSON */ }
      setError(msg);
      setBusy(false);
    }
  };

  const range = parsed
    ? `${parsed.emailIds[0]}–${parsed.emailIds[parsed.emailIds.length - 1]}`
    : "";

  return (
    <div className="modal" onClick={onClose}>
      <div className="mbox" role="dialog" aria-label="Upload forecast" onClick={(e) => e.stopPropagation()}>
        <div className="mbhd">
          <Upload size={16} />
          <div style={{ minWidth: 0 }}>
            <div className="mt">Upload Forecast</div>
            <div className="ms">Externally-generated prediction JSON for this corpus</div>
          </div>
          <button className="icon-btn" style={{ marginLeft: "auto" }} title="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div style={{ padding: 18, overflowY: "auto" }}>
          <div
            className="fcst-drop"
            data-over={dragOver || undefined}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) readFile(f);
            }}
            onClick={() => fileRef.current?.click()}
          >
            <Upload size={18} style={{ color: "var(--tert)" }} />
            <div style={{ fontSize: 13, color: "var(--muted)" }}>
              Drop a <span className="mono">.json</span> forecast here, or click to browse
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".json,application/json"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) readFile(f);
                e.target.value = "";
              }}
            />
          </div>
          {error && (
            <div className="detail" role="alert" style={{ color: "var(--red)" }}>
              {error}
            </div>
          )}
          {parsed && (
            <div className="detail">
              <div style={{ fontWeight: 600, fontSize: 13 }}>{parsed.fileName}</div>
              <div style={{ marginTop: 4, color: "var(--muted)" }}>
                {parsed.snapshots.length} snapshot{parsed.snapshots.length === 1 ? "" : "s"} parsed,
                covering emails {range}
                {parsed.generatedBy ? ` · generated by ${parsed.generatedBy}` : ""}
              </div>
            </div>
          )}
        </div>
        <div className="mbft">
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={confirm} disabled={!parsed || busy}>
            {busy ? <Loader2 size={13} className="spinrot" /> : <Upload size={13} />} Upload
          </button>
        </div>
      </div>
    </div>
  );
}
